import { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ShieldCheck } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [s, setS] = useState(null);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);

  const save = async () => {
    try {
      const r = await api.patch("/settings", {
        test_mode: s.test_mode,
        admin_test_email: s.admin_test_email,
        timezone: s.timezone,
        quiet_hours_start: s.quiet_hours_start,
        quiet_hours_end: s.quiet_hours_end,
        default_reminder_days_before: s.default_reminder_days_before,
        require_approval: s.require_approval,
      });
      setS(r.data);
      toast.success("Settings saved");
      // Force reload to update banner
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (!s) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  return (
    <div>
      <PageHeader testid="page-settings" title="Settings" subtitle="Admin-only · governs reminder engine behaviour" />
      <div className="p-6 lg:p-8 max-w-3xl space-y-6">
        <section className="border border-slate-200 rounded-md bg-white p-5">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="h-4 w-4 text-slate-700" />
            <h2 className="font-heading text-base font-semibold text-slate-900">Practice-inbox review</h2>
          </div>
          <div className="flex items-start justify-between gap-4">
            <div className="text-sm text-slate-700 max-w-lg">
              While this is on (the default), <strong>all outgoing chasers and reminders route to your Taxegy inbox</strong> instead of
              the client. Review each one, then forward it yourself. Turn it off only when you're ready to let Taxegy email clients directly.
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="test-mode-switch" checked={s.test_mode} onCheckedChange={(v) => setS({ ...s, test_mode: v })} disabled={!isAdmin} />
              <span className="text-sm font-mono">{s.test_mode ? "ON" : "OFF"}</span>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Practice inbox (all reminders and reviewed chasers go here)</Label>
              <Input data-testid="admin-email-input" value={s.admin_test_email} onChange={(e) => setS({ ...s, admin_test_email: e.target.value })} disabled={!isAdmin} />
              <div className="text-[11px] text-slate-500 mt-1">Reminders always come here — chasers route here too while review mode is on.</div>
            </div>
            <div>
              <Label>Timezone (display)</Label>
              <Input value={s.timezone} onChange={(e) => setS({ ...s, timezone: e.target.value })} disabled={!isAdmin} />
              <div className="text-[11px] text-slate-500 mt-1">Timestamps stored in UTC.</div>
            </div>
          </div>
        </section>

        <section className="border border-slate-200 rounded-md bg-white p-5">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="h-4 w-4 text-slate-700" />
            <h2 className="font-heading text-base font-semibold text-slate-900">Reminder engine</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Quiet hours start (UK)</Label>
              <Input value={s.quiet_hours_start} onChange={(e) => setS({ ...s, quiet_hours_start: e.target.value })} disabled={!isAdmin} />
            </div>
            <div>
              <Label>Quiet hours end (UK)</Label>
              <Input value={s.quiet_hours_end} onChange={(e) => setS({ ...s, quiet_hours_end: e.target.value })} disabled={!isAdmin} />
            </div>
            <div className="md:col-span-2">
              <Label>Default reminder triggers (days before deadline, comma-separated)</Label>
              <Input
                data-testid="reminder-days-input"
                value={(s.default_reminder_days_before || []).join(", ")}
                onChange={(e) => setS({ ...s, default_reminder_days_before: e.target.value.split(",").map((x) => parseInt(x.trim(), 10)).filter(Boolean) })}
                disabled={!isAdmin}
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-3">
              <Switch data-testid="require-approval-switch" checked={s.require_approval} onCheckedChange={(v) => setS({ ...s, require_approval: v })} disabled={!isAdmin} />
              <span className="text-sm">Require manual approval before sending</span>
            </div>
          </div>
        </section>

        {isAdmin ? (
          <div className="flex justify-end">
            <Button data-testid="save-settings-btn" onClick={save} className="rounded-md bg-slate-900 hover:bg-slate-800">Save settings</Button>
          </div>
        ) : (
          <div className="text-xs text-slate-500">Only admins can update settings.</div>
        )}
      </div>
    </div>
  );
}
